import React, { useState, useMemo } from 'react';
import { motion } from 'motion/react';
import {
  MapPin,
  Calendar,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Layers,
} from 'lucide-react';
import { MemoryItem, VisitorInfo } from '../types';
import { formatDateSpanish } from '../utils/storage';

interface MemoryCardProps {
  memory: MemoryItem;
  currentVisitor?: VisitorInfo | null;
  partner1Name?: string;
  partner2Name?: string;
  activeAdminProfile?: 'novio' | 'novia';
  layoutMode?: 'grid' | 'masonry';
  onOpenMediaLightbox: (memory: MemoryItem, initialIndex?: number) => void;
}

export const MemoryCard: React.FC<MemoryCardProps> = ({
  memory,
  currentVisitor,
  partner1Name,
  partner2Name,
  onOpenMediaLightbox,
}) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [fitMode, setFitMode] = useState<'contain' | 'cover'>('contain');
  const [mediaAspectRatios, setMediaAspectRatios] = useState<Record<number, number>>({});

  const handleMediaLoaded = (idx: number, width?: number, height?: number) => {
    if (width && height && height > 0) {
      const rawRatio = width / height;
      const clamped = Math.max(0.55, Math.min(2.2, rawRatio));
      setMediaAspectRatios((prev) => {
        if (prev[idx] === clamped) return prev;
        return { ...prev, [idx]: clamped };
      });
    }
  };

  // Swipe support for touch devices with vertical scroll protection
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);
  const [touchEndY, setTouchEndY] = useState<number | null>(null);

  const authorInfo = useMemo(() => {
    const role = memory.authorRole;
    const name = memory.authorName;
    const p1 = partner1Name || 'Jackson';
    const p2 = partner2Name || 'Laura';

    if (role === 'novio' || (!role && name && name.toLowerCase() === p1.toLowerCase())) {
      return {
        label: p1,
        subtitle: 'Novio',
        icon: '💙',
        badgeClass: 'bg-sky-50 text-sky-700 border-sky-200/80 shadow-2xs',
        dotClass: 'bg-sky-500',
      };
    }
    if (role === 'novia' || (!role && name && name.toLowerCase() === p2.toLowerCase())) {
      return {
        label: p2,
        subtitle: 'Novia',
        icon: '💖',
        badgeClass: 'bg-rose-50 text-rose-700 border-rose-200/80 shadow-2xs',
        dotClass: 'bg-rose-500',
      };
    }
    return {
      label: name || 'Nosotros',
      subtitle: 'Juntos',
      icon: '💑',
      badgeClass: 'bg-amber-50 text-amber-800 border-amber-200/80 shadow-2xs',
      dotClass: 'bg-amber-500',
    };
  }, [memory.authorRole, memory.authorName, partner1Name, partner2Name]);

  const mediaList = useMemo(() => {
    if (memory.mediaUrls && memory.mediaUrls.length > 0) {
      return memory.mediaUrls;
    }
    return [memory.mediaUrl];
  }, [memory]);

  const hasMultipleMedia = mediaList.length > 1;
  const currentMediaUrl = mediaList[currentMediaIndex] || memory.mediaUrl;

  const handleNextMedia = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSlideDirection('right');
    setCurrentMediaIndex((prev) => (prev + 1) % mediaList.length);
  };

  const handlePrevMedia = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSlideDirection('left');
    setCurrentMediaIndex((prev) => (prev - 1 + mediaList.length) % mediaList.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
    setTouchEndX(null);
    setTouchEndY(null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const distanceX = touchStartX - touchEndX;
    const distanceY = (touchStartY !== null && touchEndY !== null) ? Math.abs(touchStartY - touchEndY) : 0;

    // Si el usuario se estaba desplazando verticalmente (scroll), NO cambiar foto
    if (distanceY > Math.abs(distanceX) * 0.8) {
      return;
    }

    const minSwipeDistance = 45;
    if (distanceX > minSwipeDistance) {
      handleNextMedia();
    } else if (distanceX < -minSwipeDistance) {
      handlePrevMedia();
    }
  };

  const categoryStyles: Record<string, { label: string; icon: string; badge: string }> = {
    cita: {
      label: 'Cita Romántica',
      icon: '🌹',
      badge: 'bg-rose-500/90 text-white border-rose-400/50',
    },
    viaje: {
      label: 'Viaje Juntos',
      icon: '✈️',
      badge: 'bg-sky-500/90 text-white border-sky-400/50',
    },
    aniversario: {
      label: 'Aniversario',
      icon: '✨',
      badge: 'bg-amber-500/90 text-white border-amber-400/50',
    },
    cotidiano: {
      label: 'Día a Día',
      icon: '☕',
      badge: 'bg-emerald-600/90 text-white border-emerald-400/50',
    },
    especial: {
      label: 'Momento Especial',
      icon: '💫',
      badge: 'bg-purple-600/90 text-white border-purple-400/50',
    },
  };

  const cat = categoryStyles[memory.category] || categoryStyles.especial;
  const currentRatio = mediaAspectRatios[currentMediaIndex] || 1;

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="group bg-white rounded-3xl overflow-hidden border border-slate-200/90 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.06)] hover:shadow-[0_20px_42px_-8px_rgba(0,0,0,0.12)] hover:border-rose-200/90 hover:-translate-y-1 transition-all duration-300 flex flex-col relative"
      id={`memory-card-${memory.id}`}
    >
      {/* 1. VISOR DE FOTOGRAFÍA O VIDEO CON ASPECTO NATURAL */}
      <div
        className="relative w-full bg-slate-900 overflow-hidden cursor-pointer select-none flex items-center justify-center transition-all duration-300 touch-pan-y"
        style={{
          aspectRatio: currentRatio ? `${currentRatio}` : '1 / 1',
          maxHeight: '560px',
          minHeight: '270px',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => onOpenMediaLightbox(memory, currentMediaIndex)}
      >
        {/* Active Media Item with Slide Animation */}
        <motion.div
          key={currentMediaIndex}
          initial={{ opacity: 0.85, x: slideDirection === 'right' ? 24 : -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.24, ease: 'easeOut' }}
          className="relative w-full h-full flex items-center justify-center bg-slate-950 overflow-hidden"
        >
          {/* Ambient Backdrop para evitar bordes duros */}
          {typeof currentMediaUrl === 'string' && !currentMediaUrl.startsWith('data:video') && (
            <div
              className="absolute inset-0 bg-cover bg-center blur-2xl opacity-40 scale-125 pointer-events-none transition-all duration-300"
              style={{ backgroundImage: `url(${currentMediaUrl})` }}
            />
          )}

          {memory.type === 'video' ||
          (typeof currentMediaUrl === 'string' && currentMediaUrl.startsWith('data:video')) ? (
            <video
              src={currentMediaUrl}
              onLoadedMetadata={(e) => {
                const video = e.currentTarget;
                handleMediaLoaded(currentMediaIndex, video.videoWidth, video.videoHeight);
              }}
              className={`relative z-10 w-full h-full ${
                fitMode === 'cover' ? 'object-cover' : 'object-contain'
              }`}
              controls
              playsInline
            />
          ) : (
            <img
              src={currentMediaUrl}
              alt={`${memory.place || 'Recuerdo'} - foto ${currentMediaIndex + 1}`}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              ref={(node) => {
                if (node && node.complete && node.naturalWidth && node.naturalHeight) {
                  handleMediaLoaded(currentMediaIndex, node.naturalWidth, node.naturalHeight);
                }
              }}
              onLoad={(e) => {
                const img = e.currentTarget;
                handleMediaLoaded(currentMediaIndex, img.naturalWidth, img.naturalHeight);
              }}
              className={`relative z-10 w-full h-full transition-transform duration-500 group-hover:scale-[1.015] drop-shadow-md hardware-accelerated ${
                fitMode === 'cover' ? 'object-cover' : 'object-contain'
              }`}
            />
          )}
        </motion.div>

        {/* Top Badges Bar: Categoría & Indicador Multitoma */}
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none z-20">
          {/* Categoría */}
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md border shadow-md flex items-center gap-1.5 pointer-events-auto ${cat.badge}`}
          >
            <span>{cat.icon}</span>
            <span>{cat.label}</span>
          </span>

          {/* Carrusel badge */}
          {hasMultipleMedia && (
            <div className="px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-xs font-medium flex items-center gap-1.5 shadow-md pointer-events-auto">
              <Layers className="w-3.5 h-3.5 text-amber-300" />
              <span>
                {currentMediaIndex + 1} / {mediaList.length}
              </span>
            </div>
          )}
        </div>

        {/* Botones de navegación del carrusel */}
        {hasMultipleMedia && (
          <button
            type="button"
            onClick={handlePrevMedia}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/45 hover:bg-black/80 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 sm:opacity-85 transition-all hover:scale-110 active:scale-95 cursor-pointer shadow-lg border border-white/15"
            aria-label="Foto anterior"
            title="Foto anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}

        {hasMultipleMedia && (
          <button
            type="button"
            onClick={handleNextMedia}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/45 hover:bg-black/80 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 sm:opacity-85 transition-all hover:scale-110 active:scale-95 cursor-pointer shadow-lg border border-white/15"
            aria-label="Foto siguiente"
            title="Foto siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        {/* Indicadores de puntos inferiores */}
        {hasMultipleMedia && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/50 backdrop-blur-md border border-white/15 max-w-[85%] overflow-x-auto no-scrollbar">
            {mediaList.length <= 12 ? (
              mediaList.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSlideDirection(idx > currentMediaIndex ? 'right' : 'left');
                    setCurrentMediaIndex(idx);
                  }}
                  className={`h-1.5 rounded-full transition-all cursor-pointer ${
                    idx === currentMediaIndex
                      ? 'w-4 bg-amber-300'
                      : 'w-1.5 bg-white/50 hover:bg-white/80'
                  }`}
                  aria-label={`Ir a foto ${idx + 1}`}
                />
              ))
            ) : (
              <span className="text-[10px] font-medium text-white/90 px-1">
                {currentMediaIndex + 1} de {mediaList.length}
              </span>
            )}
          </div>
        )}

        {/* Controles de Vista Rápida (Ajuste y Pantalla Completa) */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5 z-20">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setFitMode((prev) => (prev === 'contain' ? 'cover' : 'contain'));
            }}
            title={fitMode === 'contain' ? 'Modo actual: Completo • Clic para rellenar' : 'Modo actual: Relleno • Clic para ver completo'}
            className={`p-1.5 sm:p-2 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all hover:scale-110 active:scale-95 cursor-pointer shadow-md border ${
              fitMode === 'contain'
                ? 'bg-amber-500/85 text-white border-amber-300/60'
                : 'bg-black/60 hover:bg-black/85 text-white border-white/15'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={() => onOpenMediaLightbox(memory, currentMediaIndex)}
            title="Ver fotografía en pantalla completa"
            className="p-1.5 sm:p-2 rounded-full bg-black/60 hover:bg-black/85 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all hover:scale-110 active:scale-95 cursor-pointer shadow-md border border-white/15"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* 2. ZONA EDITORIAL DE DETALLES Y MENSAJE */}
      <div className="p-5 sm:p-6 flex flex-col flex-1 justify-between bg-white">
        <div>
          {/* Fila de metadatos: Autor, Lugar y Fecha */}
          <div className="flex flex-wrap items-center gap-2 pb-3.5 border-b border-slate-100">
            {/* Badge de Autor */}
            {authorInfo && (
              <div
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${authorInfo.badgeClass}`}
                title={`Publicado con amor por ${authorInfo.label} (${authorInfo.subtitle})`}
              >
                <span>{authorInfo.icon}</span>
                <span className="truncate max-w-[150px]">{authorInfo.label}</span>
              </div>
            )}

            {/* Lugar con Pin */}
            <div
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200/70 px-2.5 py-1 rounded-full transition-colors cursor-default"
              title={
                memory.locationSource === 'photo_exif'
                  ? `Ubicación obtenida de la foto: ${memory.locationName || memory.place}${
                      memory.latitude ? ` (${memory.latitude.toFixed(4)}°, ${memory.longitude?.toFixed(4)}°)` : ''
                    }`
                  : memory.locationSource === 'current_gps'
                  ? `GPS actual en vivo: ${memory.locationName || memory.place}${
                      memory.accuracyMeters ? ` (Precisión: ±${memory.accuracyMeters}m)` : ''
                    }`
                  : memory.place || 'Lugar especial'
              }
            >
              <MapPin className="w-3 h-3 text-rose-500 shrink-0" />
              <span className="truncate max-w-[150px]">
                {memory.place || 'Lugar especial'}
              </span>
              {memory.locationSource === 'photo_exif' && (
                <span
                  className="text-[9px] font-bold text-amber-700 bg-amber-100/90 border border-amber-300/60 px-1.5 py-0.2 rounded-md"
                  title="Ubicación obtenida de la foto"
                >
                  Foto
                </span>
              )}
              {memory.locationSource === 'current_gps' && (
                <span
                  className="w-2 h-2 rounded-full bg-emerald-500 shrink-0 shadow-2xs"
                  title="Ubicación GPS en tiempo real"
                />
              )}
            </div>

            {/* Fecha */}
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200/70 px-2.5 py-1 rounded-full">
              <Calendar className="w-3 h-3 text-amber-500 shrink-0" />
              <span>{formatDateSpanish(memory.date)}</span>
            </div>
          </div>

          {/* MENSAJE DE AMOR (Tipografía Clara, Limpia y 100% Entendible) */}
          <div className="mt-3.5 relative pl-3 border-l-2 border-rose-400">
            <p className="text-[14.5px] sm:text-[15.5px] text-slate-800 font-medium leading-relaxed">
              &ldquo;{memory.message}&rdquo;
            </p>
          </div>
        </div>
      </div>
    </motion.article>
  );
};
